(() => {
  // Safe when the section asset is emitted more than once on the same page.
  if (customElements.get('ks-fancy-slideshow')) return;
class KsFancySlideshow extends HTMLElement {
  constructor() {
    super();
    this.sliderElement = null;
    this.swiperJsPath = '';
    this.swiper = null;
    this.bootObserver = null;
    this.activityObserver = null;
    this.scrollObserver = null;
    this.entryObserver = null;
    this.abortController = null;
    this.hasBooted = false;
    this.mountId = 0;
    this.editorTimer = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    if (this.hasBooted) return;
    this.mountId += 1;
    this.sliderElement = this.querySelector('.swiper');
    this.swiperJsPath = this.dataset.vendorJsFile;
    if (!this.sliderElement) return;

    this.abortController = new AbortController();
    this.setupEntryAnimation();

    const sectionIndex = Number(this.dataset.sectionIndex || 1);
    const shouldBootImmediately =
      window.Shopify?.designMode || sectionIndex <= 1 || !('IntersectionObserver' in window);

    if (shouldBootImmediately) {
      this.boot();
      return;
    }

    this.bootObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.bootObserver?.disconnect();
        this.bootObserver = null;
        this.boot();
      },
      { rootMargin: '420px 0px' }
    );
    this.bootObserver.observe(this);
  }

  disconnectedCallback() {
    this.mountId += 1;
    clearTimeout(this.editorTimer);
    this.editorTimer = null;
    this.bootObserver?.disconnect();
    this.activityObserver?.disconnect();
    this.scrollObserver?.disconnect();
    this.entryObserver?.disconnect();
    this.abortController?.abort();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.pauseAllVideos();
    this.bootObserver = null;
    this.activityObserver = null;
    this.scrollObserver = null;
    this.entryObserver = null;
    this.abortController = null;
    this.hasBooted = false;
  }

  async boot() {
    if (this.hasBooted || !this.isConnected || !this.sliderElement) return;
    this.hasBooted = true;
    const mountId = this.mountId;

    try {
      await this.loadVendorScript();
      if (!this.isConnected || mountId !== this.mountId) return;
      this.initSwiper();
      this.initScrollAnimation();
      this.initActivityObserver();
      this.initEditorListeners();
    } catch (error) {
      if (mountId !== this.mountId) return;
      this.hasBooted = false;
      console.error('Fancy Slideshow Error:', error);
    }
  }

  loadVendorScript() {
    if (window.Swiper) return Promise.resolve();
    if (window.__bnSwiperPromise) return window.__bnSwiperPromise;

    window.__bnSwiperPromise = this.loadScript(this.swiperJsPath, 'bn-swiper-vendor').catch((error) => {
      window.__bnSwiperPromise = null;
      throw error;
    });
    return window.__bnSwiperPromise;
  }

  getAutoplayDelayMs() {
    const seconds = Number.parseFloat(this.sliderElement?.dataset.swiperAutoplay || '0');
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
  }

  getTransitionSpeedMs() {
    const speed = Number.parseInt(this.sliderElement?.dataset.swiperSpeed || '700', 10);
    return Number.isFinite(speed) && speed >= 0 ? speed : 700;
  }

  getTransitionEasing() {
    const presets = {
      smooth: 'cubic-bezier(0.22, 1, 0.36, 1)',
      balanced: 'cubic-bezier(0.4, 0, 0.2, 1)',
      cinematic: 'cubic-bezier(0.16, 1, 0.3, 1)',
      linear: 'linear',
    };
    return presets[this.sliderElement?.dataset.swiperEasing] || presets.smooth;
  }

  getEffect() {
    const requested = this.sliderElement?.dataset.swiperEffect || 'slide';
    return requested === 'none' ? 'slide' : requested;
  }

  isPartialEnabledFor(surface) {
    if (this.sliderElement?.dataset.swiperPartial !== 'true') return false;
    const visibility = this.sliderElement?.dataset.swiperPartialVisibility || 'all';
    const normalized = String(surface || '').toLowerCase();
    if (visibility === 'all') return true;
    return visibility.split('-').includes(normalized);
  }

  getSlidesPerView(surface) {
    if (!this.isPartialEnabledFor(surface)) return 1;

    const rawPeek = Number.parseFloat(this.sliderElement?.dataset[`swiperPeek${surface}`] || '0');
    const peek = Number.isFinite(rawPeek) ? Math.min(45, Math.max(0, rawPeek)) : 0;
    if (peek <= 0) return 1;
    return Number((100 / (100 - peek)).toFixed(3));
  }

  getSpacing(surface) {
    if (this.sliderElement?.dataset.swiperSpacing !== 'true') return 0;
    const value = Number.parseFloat(this.sliderElement?.dataset[`swiperSpacing${surface}`] || '0');
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  }

  initSwiper() {
    if (this.swiper || !this.isConnected) return;
    const autoplayDelay = this.getAutoplayDelayMs();
    const transitionSpeed = this.getTransitionSpeedMs();
    const transitionEasing = this.getTransitionEasing();
    const pauseOnMouseEnter = this.sliderElement.dataset.swiperPauseHover !== 'false';
    const loop = this.sliderElement.dataset.swiperLoop !== 'false' && this.querySelectorAll('.swiper-slide').length > 1;
    const allowTouchMove = this.sliderElement.dataset.swiperTouch !== 'false';
    const mousewheelEnabled = this.sliderElement.dataset.swiperMousewheel === 'true';
    const paginationMode = this.sliderElement.dataset.swiperPagination || '';
    const paginationEnabled = ['bullets', 'fraction'].includes(paginationMode);
    const paginationType = paginationEnabled ? paginationMode : 'bullets';

    this.sliderElement.style.setProperty('--swiper-wrapper-transition-timing-function', transitionEasing);

    const autoplay =
      autoplayDelay > 0
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter,
            waitForTransition: true,
          }
        : false;

    this.swiper = new window.Swiper(this.sliderElement, {
      navigation: {
        enabled: this.sliderElement.dataset.swiperNavigation === 'true',
        prevEl: this.querySelector('.swiper-button-prev'),
        nextEl: this.querySelector('.swiper-button-next'),
      },
      pagination: {
        enabled: paginationEnabled,
        el: this.querySelector('.swiper-pagination'),
        type: paginationType,
        clickable: paginationMode === 'bullets',
      },
      scrollbar: {
        enabled: paginationMode === 'scrollbar',
        el: this.querySelector('.swiper-scrollbar'),
        draggable: true,
      },
      effect: this.getEffect(),
      speed: transitionSpeed,
      autoplay,
      loop,
      allowTouchMove,
      breakpoints: {
        0: {
          slidesPerView: this.getSlidesPerView('Mobile'),
          spaceBetween: this.getSpacing('Mobile'),
        },
        750: {
          slidesPerView: this.getSlidesPerView('Tablet'),
          spaceBetween: this.getSpacing('Tablet'),
        },
        990: {
          slidesPerView: this.getSlidesPerView('Desktop'),
          spaceBetween: this.getSpacing('Desktop'),
        },
      },
      creativeEffect: {
        prev: { translate: [0, 0, -400] },
        next: { translate: ['100%', 0, 0] },
      },
      coverflowEffect: {
        rotate: 14,
        stretch: 0,
        depth: 120,
        modifier: 1,
        slideShadows: false,
      },
      cubeEffect: {
        shadow: false,
        slideShadows: false,
      },
      flipEffect: {
        slideShadows: false,
        limitRotation: true,
      },
      centeredSlides: true,
      watchSlidesProgress: true,
      autoHeight: this.sliderElement.dataset.mediaAdapt === 'true',
      fadeEffect: { crossFade: true },
      mousewheel: { enabled: mousewheelEnabled, forceToAxis: true, releaseOnEdges: true },
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Diapositiva anterior',
        nextSlideMessage: 'Diapositiva siguiente',
        firstSlideMessage: 'Primera diapositiva',
        lastSlideMessage: 'Última diapositiva',
        paginationBulletMessage: 'Ir a la diapositiva {{index}}',
      },
      grabCursor: allowTouchMove,
      on: {
        init: () => this.syncActiveState(),
        slideChangeTransitionStart: () => this.syncActiveState(),
        slideChangeTransitionEnd: () => this.syncActiveState(),
        resize: () => this.syncSlideVideos(),
      },
    });

    this.syncActiveState();
  }

  syncActiveState() {
    if (!this.swiper) return;
    this.dataset.activeIndex = String(this.swiper.realIndex ?? 0);
    this.syncSlideVideos();
  }

  syncSlideVideos() {
    const videos = Array.from(this.querySelectorAll('.swiper-slide video'));
    if (!videos.length) return;

    const rect = this.getBoundingClientRect();
    const inViewport = this.isConnected && rect.bottom > 0 && rect.top < window.innerHeight;
    videos.forEach((video) => {
      const active = video.closest('.swiper-slide')?.classList.contains('swiper-slide-active');
      if (active && inViewport && !document.hidden && video.getClientRects().length > 0) {
        video.muted = true;
        const playPromise = video.play?.();
        if (playPromise?.catch) playPromise.catch(() => {});
      } else {
        video.pause?.();
      }
    });
  }

  pauseAllVideos() {
    this.querySelectorAll?.('video').forEach?.((video) => video.pause?.());
  }

  initActivityObserver() {
    if (!this.swiper || window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      return;
    }

    this.activityObserver = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.05);
        this.setAutoplayActive(isVisible && !document.hidden);
        if (isVisible && !document.hidden) this.syncSlideVideos();
        else this.pauseAllVideos();
      },
      { threshold: [0, 0.05, 0.25] }
    );

    this.activityObserver.observe(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.handleVisibilityChange();
  }

  setAutoplayActive(active) {
    if (!this.swiper?.autoplay || this.getAutoplayDelayMs() === 0) return;
    if (active) this.swiper.autoplay.start();
    else this.swiper.autoplay.stop();
  }

  handleVisibilityChange() {
    if (!this.swiper) return;
    const rect = this.getBoundingClientRect();
    const active = !document.hidden && rect.bottom > 0 && rect.top < window.innerHeight;
    this.setAutoplayActive(active);
    if (active) this.syncSlideVideos();
    else this.pauseAllVideos();
  }

  initScrollAnimation() {
    if (
      this.dataset.overlayScroll !== 'true' ||
      this.reducedMotion.matches ||
      !('IntersectionObserver' in window)
    ) return;

    const mediaWrappers = Array.from(this.querySelectorAll('.ks-media-wrapper'));
    if (!mediaWrappers.length) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const ratio = Math.max(0, Math.min(1, entry.intersectionRatio));
        mediaWrappers.forEach((elem) => {
          const targetOpacity = Number(elem.dataset.overlayOpacity || 0);
          const finalOpacity = 100 - ratio * (100 - targetOpacity);
          elem.style.setProperty('--overlay-opacity', `${finalOpacity}%`);
        });
      },
      { threshold: [0, 0.15, 0.3, 0.5, 0.75, 1] }
    );

    this.scrollObserver.observe(this);
  }

  setupEntryAnimation() {
    const type = this.dataset.entryAnimation || 'none';
    if (type === 'none') {
      this.classList.add('is-entry-visible');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      requestAnimationFrame(() => this.classList.add('is-entry-visible'));
      return;
    }

    this.entryObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-entry-visible')));
        this.entryObserver?.disconnect();
        this.entryObserver = null;
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    this.entryObserver.observe(this);
  }

  initEditorListeners() {
    if (!window.Shopify?.designMode || !this.abortController) return;
    const signal = this.abortController.signal;

    this.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!this.swiper) return;
        const slide = event.target.closest('.swiper-slide');
        if (!slide) return;

        const slideIndex = Number.parseInt(slide.dataset.index, 10);
        if (!Number.isFinite(slideIndex)) return;

        if (typeof this.swiper.slideToLoop === 'function' && this.swiper.params.loop) {
          this.swiper.slideToLoop(slideIndex, this.getTransitionSpeedMs());
        } else {
          this.swiper.slideTo(slideIndex, this.getTransitionSpeedMs());
        }

        if (this.getAutoplayDelayMs() > 0 && this.swiper.autoplay) {
          clearTimeout(this.editorTimer);
          this.editorTimer = window.setTimeout(() => {
            if (!signal.aborted && this.isConnected && this.swiper?.autoplay) this.swiper.autoplay.start();
          }, Math.max(120, this.getTransitionSpeedMs()));
        }
      },
      { signal }
    );

    this.addEventListener(
      'shopify:section:select',
      () => {
        this.classList.add('is-entry-visible');
        this.swiper?.update?.();
        this.syncSlideVideos();
      },
      { signal }
    );
  }

  loadScript(src, id = 'bn-swiper-vendor') {
    return new Promise((resolve, reject) => {
      if (!src) { reject(new Error('Swiper asset URL is missing.')); return; }
      if (window.Swiper) { resolve(); return; }
      const existing = document.getElementById(id) || document.getElementById('swiper-js');
      const script = existing || document.createElement('script');
      let timeout;
      const cleanup = () => {
        clearTimeout(timeout);
        script.removeEventListener('load', loaded);
        script.removeEventListener('error', failed);
      };
      const failed = () => {
        cleanup();
        // A failed script cannot fire a future load event. Remove it so retry can work.
        script.remove();
        reject(new Error('Swiper asset could not be loaded.'));
      };
      const loaded = () => {
        if (!window.Swiper) { failed(); return; }
        cleanup();
        resolve();
      };
      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', failed, { once: true });
      timeout = setTimeout(failed, 20000);
      if (!existing) {
        script.src = src;
        script.id = id;
        script.async = true;
        document.head.appendChild(script);
      }
    });
  }

}

if (!customElements.get('ks-fancy-slideshow')) {
  customElements.define('ks-fancy-slideshow', KsFancySlideshow);
}

})();
